import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";

import Button from "@/components/Button";
import { PageHeading, SectionHeading, SrOnly } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import { ResponsiveTable, Table, TableContent, Th } from "@/components/Table";
import TableRow from "./components/TableRow";
import { api } from "@/utils/api";
import { MetaBusinessAccountResource } from "@/types/resources";
import Form from "./components/Form";
import UnassignedChannelsCard from "./components/UnassignedChannelsCard";

const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const NewProjectButton = styled(Button)`
  padding: 0 32px;
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    vertical-align: center;
    &:first-child {
      padding-left: 24px;
    }
  }
`;

const MetaBusinessAccounts = () => {
  const [showModal, setShowModal] = useState(false);

  const {
    data: accounts,
    isError,
    isLoading,
  } = useQuery(
    ["meta_business_accounts"],
    () =>
      api.get<never, MetaBusinessAccountResource[]>(
        "/v1/meta-business-accounts"
      ),
    {
      staleTime: 120 * 1000,
    }
  );

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <HeadingsRow>
        <PageHeading>Meta Business Accounts</PageHeading>
        <NewProjectButton onClick={() => setShowModal(true)}>
          Create New
        </NewProjectButton>
      </HeadingsRow>
      <UnassignedChannelsCard />
      <SectionHeading>Accounts List</SectionHeading>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Account Name</Th>
              <Th>Date Created</Th>
              <Th>Pages Quantity</Th>
              <Th>Note</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!accounts?.length}
              colSpan={5}
            >
              {accounts?.map((acc) => (
                <TableRow key={acc.id} account={acc} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <AnimatePresence>
        {showModal && <Form onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </>
  );
};

export default MetaBusinessAccounts;
